const RECORDS_KEY = 'ondoku_records';
const CUSTOM_TEXTS_KEY = 'ondoku_custom_texts';

// ゲームキーは子どもごとに分ける（兄弟共有を防ぐ）
const gameKey = (childId) => `ondoku_game_${childId || 'guest'}`;

/** カスタムテキストを保存 */
export function saveCustomText(text) {
  const texts = loadCustomTexts();
  const newText = { ...text, id: `custom_${Date.now()}`, createdAt: new Date().toISOString() };
  texts.push(newText);
  localStorage.setItem(CUSTOM_TEXTS_KEY, JSON.stringify(texts));
  return newText;
}

/** カスタムテキストを全件取得 */
export function loadCustomTexts() {
  try {
    return JSON.parse(localStorage.getItem(CUSTOM_TEXTS_KEY) || '[]');
  } catch {
    return [];
  }
}

/** カスタムテキストを更新 */
export function updateCustomText(id, updated) {
  const texts = loadCustomTexts().map((t) =>
    t.id === id ? { ...t, ...updated, id, updatedAt: new Date().toISOString() } : t
  );
  localStorage.setItem(CUSTOM_TEXTS_KEY, JSON.stringify(texts));
  return texts.find((t) => t.id === id);
}

/** カスタムテキストを削除 */
export function deleteCustomText(id) {
  const texts = loadCustomTexts().filter((t) => t.id !== id);
  localStorage.setItem(CUSTOM_TEXTS_KEY, JSON.stringify(texts));
}

function toLocalDateStr(date) {
  // toISOString()はUTCなので、ローカル時刻で YYYY-MM-DD を返す
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function today() {
  return toLocalDateStr(new Date());
}

function thisMonth() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

/** 記録を保存（保存したレコードを返す） */
export function saveRecord(record, childId) {
  const records = getRecords();
  const id = `rec_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
  const saved = { ...record, id, localDate: today(), childId: childId ?? null };
  records.push(saved);
  localStorage.setItem(RECORDS_KEY, JSON.stringify(records));
  // ゲーム解放は「ほんばん」60点以上完了時のみ（子どもごとに管理）
  if (record.mode === 'honban' && (record.avgScore ?? 0) >= 60) {
    const key = gameKey(childId);
    const gameData = JSON.parse(localStorage.getItem(key) || '{}');
    if (gameData.date !== today()) {
      localStorage.setItem(key, JSON.stringify({ date: today(), played: false }));
    }
  }
  return saved;
}

/**
 * 全記録取得
 * childId を渡すと、その子の記録に絞り込む（きょうだいで端末を共有しても混ざらないように）。
 * 省略時は従来通り端末内の全件を返す（ゲスト→ログイン移行など、単一プロフィール前提の箇所向け）。
 *
 * デフォルトでは childId 未タグ（2026-08-19のchildId埋め込み対応より前に保存された記録）も
 * 一緒に含める。表示だけなら「誰のものか分からない古い記録」を見せておく方が親切なため。
 * strict:true を指定すると、この子に明示的にタグ付けされたものだけに絞る
 * （クラウドへ自動移行するような「書き込みを伴う」箇所では、こちらを使ってきょうだいの
 *  記録を誤って移行しないようにする）。
 */
export function getRecords(childId, { strict = false } = {}) {
  let records;
  try {
    records = JSON.parse(localStorage.getItem(RECORDS_KEY) || '[]');
  } catch {
    records = [];
  }
  if (childId === undefined) return records;
  if (strict) return records.filter((r) => r.childId === childId);
  return records.filter((r) => r.childId === childId || r.childId == null);
}

/** 統計を計算 */
export function loadStats() {
  const records = getRecords();

  // 連続日数
  const dates = [...new Set(records.map((r) => r.localDate))].sort();
  let streak = 0;
  let check = today();
  for (let i = dates.length - 1; i >= 0; i--) {
    if (dates[i] === check) {
      streak++;
      const d = new Date(check);
      d.setDate(d.getDate() - 1);
      check = d.toISOString().slice(0, 10);
    } else {
      break;
    }
  }

  // 合計スター
  const totalStars = records.reduce((s, r) => s + (r.totalStars ?? 0), 0);

  // 今月の日数
  const monthDays = new Set(
    records.filter((r) => r.localDate?.startsWith(thisMonth())).map((r) => r.localDate)
  ).size;

  return { streak, totalStars, monthDays };
}

/** ゲームが今日プレイ可能か（子どもごとに判定） */
export function canPlayGame(childId) {
  try {
    const gameData = JSON.parse(localStorage.getItem(gameKey(childId)) || '{}');
    return gameData.date === today() && !gameData.played;
  } catch {
    return false;
  }
}

/** 今日すでにゲームを遊んだか（子どもごとに判定） */
export function isGamePlayedToday(childId) {
  try {
    const gameData = JSON.parse(localStorage.getItem(gameKey(childId)) || '{}');
    return gameData.date === today() && gameData.played === true;
  } catch {
    return false;
  }
}

/** ゲームプレイ済みにする（子どもごとに記録） */
export function markGamePlayed(childId) {
  const key = gameKey(childId);
  const gameData = JSON.parse(localStorage.getItem(key) || '{}');
  localStorage.setItem(key, JSON.stringify({ ...gameData, played: true }));
}
